import React, { useRef, useState, useEffect, useCallback } from 'react';

interface MarqueeTextProps {
  text: string;
  className?: string;
  activeClassName?: string;
  speed?: number; // pixels per second (comfortable reading speed ~30-35px/s)
  pauseDelay?: number; // seconds to pause at beginning and end
}

export const MarqueeText: React.FC<MarqueeTextProps> = ({
  text,
  className = '',
  activeClassName = '',
  speed = 35,
  pauseDelay = 0.8,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflowDistance, setOverflowDistance] = useState(0);

  const measure = useCallback(() => {
    if (containerRef.current && textRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const textWidth = textRef.current.scrollWidth;
      if (textWidth > containerWidth + 2) {
        setOverflowDistance(textWidth - containerWidth);
      } else {
        setOverflowDistance(0);
      }
    }
  }, []);

  useEffect(() => {
    measure();
    const handleResize = () => measure();
    window.addEventListener('resize', handleResize);
    const timer = setTimeout(measure, 120);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [text, measure]);

  const hasOverflow = overflowDistance > 0;
  // Calculate duration so longer text scrolls at the same readable speed
  const scrollDuration = hasOverflow ? Math.max(2.5, overflowDistance / speed) + (pauseDelay * 2) : 0;

  return (
    <div
      ref={containerRef}
      className={`overflow-hidden whitespace-nowrap max-w-full relative ${className}`}
      onMouseEnter={measure}
      onFocus={measure}
      title={text}
    >
      <span
        ref={textRef}
        className={`inline-block whitespace-nowrap will-change-transform transition-transform duration-300 ${
          hasOverflow ? 'marquee-auto-scroll' : ''
        } ${activeClassName}`}
        style={
          hasOverflow
            ? ({
                '--marquee-distance': `-${overflowDistance + 6}px`,
                '--marquee-duration': `${scrollDuration}s`,
                '--marquee-delay': `${pauseDelay}s`,
              } as React.CSSProperties)
            : undefined
        }
      >
        {text}
      </span>
    </div>
  );
};
